// Enemy avatars: one shared humanoid rig with per-type colors and a signature
// accessory each (v1 obstacle-manager.js had six ~170-line bespoke builders;
// v2 makes them data-driven — punk mohawk, hipster beanie, business briefcase,
// jogger headband, artist spray can, pedestrian umbrella).

import * as THREE from 'three';
import type { EnemyDef } from '@/content/enemies';
import type { Rng } from '@/core/rng';

export interface EnemyRig {
  group: THREE.Group;
  leftArm: THREE.Object3D;
  rightArm: THREE.Object3D;
  leftLeg: THREE.Object3D;
  rightLeg: THREE.Object3D;
  head: THREE.Object3D;
  torso: THREE.Object3D;
}

const SKIN_TONES = [0xd9a066, 0xc68642, 0x8d5524, 0xf1c27d];

// Shared geometries — every rig reuses the same buffers; only materials are
// per-enemy since colors vary (Phase 7 geometry cache)
const torsoGeo = new THREE.BoxGeometry(0.4, 0.48, 0.22);
const headGeo = new THREE.SphereGeometry(0.13, 10, 8);
const armGeo = new THREE.BoxGeometry(0.09, 0.4, 0.09).translate(0, -0.15, 0);
const legGeo = new THREE.BoxGeometry(0.12, 0.42, 0.12).translate(0, -0.17, 0);
const mohawkGeo = new THREE.BoxGeometry(0.05, 0.18, 0.24);
const beanieGeo = new THREE.CylinderGeometry(0.13, 0.14, 0.1, 8);
const coffeeGeo = new THREE.CylinderGeometry(0.04, 0.035, 0.11, 8);
const hairCapGeo = new THREE.SphereGeometry(0.13, 10, 6, 0, Math.PI * 2, 0, 1.25);
const briefcaseGeo = new THREE.BoxGeometry(0.22, 0.16, 0.06);
const headbandGeo = new THREE.CylinderGeometry(0.135, 0.135, 0.035, 10);
const wildHairGeo = new THREE.SphereGeometry(0.15, 8, 6);
const sprayCanGeo = new THREE.CylinderGeometry(0.035, 0.035, 0.12, 8);
const umbrellaShaftGeo = new THREE.CylinderGeometry(0.012, 0.012, 0.5, 6);
const umbrellaCanopyGeo = new THREE.ConeGeometry(0.2, 0.12, 8);
const neckGeo = new THREE.CylinderGeometry(0.05, 0.05, 0.1, 6);
const eyeGeo = new THREE.SphereGeometry(0.018, 4, 4);
const pupilGeo = new THREE.SphereGeometry(0.01, 4, 4);
const handGeo = new THREE.SphereGeometry(0.045, 6, 5);
const shoeGeo = new THREE.BoxGeometry(0.13, 0.07, 0.2);
const shirtGeo = new THREE.BoxGeometry(0.28, 0.36, 0.02);
const eyeWhiteMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
const pupilMat = new THREE.MeshLambertMaterial({ color: 0x000000 });
const shoeMat = new THREE.MeshLambertMaterial({ color: 0x2b2b2b });

function pick<T>(arr: readonly T[], rng: Rng): T {
  const item = arr[Math.floor(rng.next() * arr.length)];
  if (item === undefined) throw new Error('pick from empty array');
  return item;
}

function limb(
  geo: THREE.BufferGeometry,
  material: THREE.Material,
  x: number,
  y: number,
): THREE.Mesh {
  const mesh = new THREE.Mesh(geo, material);
  mesh.position.set(x, y, 0);
  mesh.castShadow = true;
  return mesh;
}

export function buildEnemyRig(def: EnemyDef, rng: Rng): EnemyRig {
  const group = new THREE.Group();
  const clothing = new THREE.MeshLambertMaterial({ color: pick(def.colors.clothing, rng) });
  const skin = new THREE.MeshLambertMaterial({ color: pick(SKIN_TONES, rng) });
  const hair = new THREE.MeshLambertMaterial({ color: pick(def.colors.hair, rng) });
  const accessory = new THREE.MeshLambertMaterial({ color: pick(def.colors.accessories, rng) });

  const torso = new THREE.Mesh(torsoGeo, clothing);
  torso.position.y = 0.62;
  torso.castShadow = true;
  group.add(torso);

  const head = new THREE.Mesh(headGeo, skin);
  head.position.y = 1.02;
  group.add(head);

  const leftArm = limb(armGeo, clothing, -0.26, 0.72);
  const rightArm = limb(armGeo, clothing, 0.26, 0.72);
  const leftLeg = limb(legGeo, clothing, -0.1, 0.36);
  const rightLeg = limb(legGeo, clothing, 0.1, 0.36);
  group.add(leftArm, rightArm, leftLeg, rightLeg);

  // Detail pass (Phase 7 graphics upgrade): neck, face, shirt accent,
  // hands and shoes — all on shared geometry buffers
  const neck = new THREE.Mesh(neckGeo, skin);
  neck.position.y = 0.9;
  group.add(neck);
  for (const x of [-0.05, 0.05]) {
    const eye = new THREE.Mesh(eyeGeo, eyeWhiteMat);
    eye.position.set(x, 1.04, 0.115);
    group.add(eye);
    const pupil = new THREE.Mesh(pupilGeo, pupilMat);
    pupil.position.set(x, 1.04, 0.125);
    group.add(pupil);
  }
  const shirt = new THREE.Mesh(shirtGeo, accessory);
  shirt.position.set(0, 0.6, 0.115);
  group.add(shirt);
  for (const arm of [leftArm, rightArm]) {
    const hand = new THREE.Mesh(handGeo, skin);
    hand.position.y = -0.34;
    arm.add(hand);
  }
  for (const leg of [leftLeg, rightLeg]) {
    const shoe = new THREE.Mesh(shoeGeo, shoeMat);
    shoe.position.set(0, -0.36, 0.03);
    leg.add(shoe);
  }

  // Hair + signature accessory
  switch (def.id) {
    case 'punk': {
      const mohawk = new THREE.Mesh(mohawkGeo, hair);
      mohawk.position.set(0, 1.18, 0);
      group.add(mohawk);
      break;
    }
    case 'hipster': {
      const beanie = new THREE.Mesh(beanieGeo, accessory);
      beanie.position.set(0, 1.13, 0);
      group.add(beanie);
      const coffee = new THREE.Mesh(coffeeGeo, accessory);
      coffee.position.set(0.05, -0.32, 0.03);
      rightArm.add(coffee);
      break;
    }
    case 'business': {
      const hairCap = new THREE.Mesh(hairCapGeo, hair);
      hairCap.position.set(0, 1.05, 0);
      group.add(hairCap);
      const briefcase = new THREE.Mesh(briefcaseGeo, accessory);
      briefcase.position.set(0, -0.38, 0);
      rightArm.add(briefcase);
      break;
    }
    case 'jogger': {
      const headband = new THREE.Mesh(headbandGeo, accessory);
      headband.position.set(0, 1.08, 0);
      group.add(headband);
      break;
    }
    case 'artist': {
      const wildHair = new THREE.Mesh(wildHairGeo, hair);
      wildHair.position.set(0, 1.12, -0.02);
      wildHair.scale.y = 0.7;
      group.add(wildHair);
      const sprayCan = new THREE.Mesh(sprayCanGeo, accessory);
      sprayCan.position.set(0, -0.34, 0.02);
      leftArm.add(sprayCan);
      break;
    }
    case 'pedestrian': {
      const hairCap = new THREE.Mesh(hairCapGeo, hair);
      hairCap.position.set(0, 1.04, 0);
      group.add(hairCap);
      const umbrella = new THREE.Group();
      const shaft = new THREE.Mesh(umbrellaShaftGeo, accessory);
      const canopy = new THREE.Mesh(umbrellaCanopyGeo, accessory);
      canopy.position.y = 0.28;
      umbrella.add(shaft, canopy);
      umbrella.position.set(0.03, -0.3, 0);
      rightArm.add(umbrella);
      break;
    }
  }

  return { group, leftArm, rightArm, leftLeg, rightLeg, head, torso };
}

/** Walk cycle (condenses v1's six apply*Animation variants: swing rate and
 *  amplitude scale with the type's speed/aggressiveness). */
export function animateEnemyRig(
  rig: EnemyRig,
  def: EnemyDef,
  time: number,
  walkSpeed: number,
): void {
  if (walkSpeed <= 0) {
    rig.leftLeg.rotation.x = 0;
    rig.rightLeg.rotation.x = 0;
    rig.leftArm.rotation.x = 0;
    rig.rightArm.rotation.x = 0;
    return;
  }
  const cycle = time * (6 + def.speed * 5);
  const amplitude = 0.55 + def.aggressiveness * 0.35;
  const swing = Math.sin(cycle);
  rig.leftLeg.rotation.x = swing * amplitude;
  rig.rightLeg.rotation.x = -swing * amplitude;
  rig.leftArm.rotation.x = -swing * amplitude * 0.8;
  rig.rightArm.rotation.x = swing * amplitude * 0.8;
  rig.torso.rotation.x = Math.abs(Math.sin(cycle * 2)) * 0.03;
}

/** Frozen combat stance (v1 obstacle-manager.js:2396-2429). */
export function combatStance(rig: EnemyRig, time: number): void {
  rig.leftLeg.rotation.x = -0.1;
  rig.leftArm.rotation.x = Math.sin(time) * 0.05 - 0.3;
  rig.torso.rotation.x = Math.sin(time * 2) * 0.02;
  rig.head.rotation.x = Math.sin(time * 1.5) * 0.02 - 0.05;
}
